import React, { memo, useEffect, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { Alert, Badge, ConfigProvider, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';
import { formatMoney } from 'services/formatMoney';
import { Colors, NetworkErrors, UserRole } from 'context';
import { Button, ButtonSizes } from 'atoms';
import { appState, authState, useMyInvoices } from 'core';
import type { IStudentInvoice } from 'core';

export const Invoices = memo(() => {
  const { t } = useTranslation();

  const { active_role, isDemoUser } = useReactiveVar(authState);
  const currentState = appState();

  const { invoices, loading, error } = useMyInvoices();

  useEffect(() => {
    if (active_role !== UserRole.STUDENT || isDemoUser) {
      appState({
        ...currentState,
        networkError: NetworkErrors.pageIsNotAvailable,
      });
    }
  }, [active_role, currentState, isDemoUser]);

  // Sum outstanding per currency — amounts are per-row currency, so we never
  // add across currencies. Rendered as the "total owed" footer; empty when the
  // student owes nothing.
  const totalsByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    invoices.forEach((invoice) => {
      const outstanding = invoice.outstanding ?? 0;
      if (outstanding > 0) {
        const currency = invoice.currency ?? '';
        totals[currency] = (totals[currency] ?? 0) + outstanding;
      }
    });
    return Object.entries(totals);
  }, [invoices]);

  const columns: ColumnsType<IStudentInvoice> = useMemo(
    () => [
      {
        title: t('invoices.columns.number'),
        dataIndex: 'number',
        key: 'number',
      },
      {
        // Face value of the invoice (priceGross), never the per-row outstanding:
        // paid invoices have outstanding === 0, which would misleadingly read as
        // "0,00". Paid/unpaid state is conveyed by the status Tag + overdue Badge
        // + the "total owed" summary below.
        //
        // ТЗ 0044: с бэка сумма приходит уже НЕТТО — за вычетом корректировок.
        // Полностью погашенные корректировкой счета сюда не доходят вовсе, а у
        // частично скорректированных сумма отличается от бумаги на руках, и без
        // пометки это выглядело бы как ошибка.
        title: t('invoices.columns.amount'),
        key: 'amount',
        render: (_, row) => (
          <AmountCell>
            <span>{formatMoney(row.priceGross ?? 0, row.currency ?? '')}</span>
            {row.isCorrected ? (
              <Tooltip title={row.correctionReason || t('invoices.correctedHint')}>
                <Tag color="blue">{t('invoices.corrected')}</Tag>
              </Tooltip>
            ) : null}
          </AmountCell>
        ),
      },
      {
        title: t('invoices.columns.dueDate'),
        key: 'dueDate',
        render: (_, row) => (
          <DueCell>
            <span>
              {row.paymentTo ? DateTime.fromISO(row.paymentTo).toFormat('dd.MM.yyyy') : ''}
            </span>
            {row.isOverdue ? (
              <Badge
                color={Colors.signal_red}
                text={t('invoices.daysOverdue', { count: row.daysOverdue ?? 0 })}
              />
            ) : null}
          </DueCell>
        ),
      },
      {
        title: t('invoices.columns.status'),
        key: 'status',
        render: (_, row) => (
          <Tag color={row.isOverdue ? 'red' : (row.outstanding ?? 0) > 0 ? 'orange' : 'green'}>
            {row.status
              ? t(`invoices.statuses.${row.status}`, {
                  defaultValue: t('invoices.statuses.unknown'),
                })
              : t('invoices.statuses.unknown')}
          </Tag>
        ),
      },
      {
        title: '',
        key: 'action',
        render: (_, row) => {
          // Prefer Fakturownia's hosted PAYMENT page over the read-only view:
          // it is what the student actually needs, and it is the same link the
          // ERP puts in reminder emails (ТЗ 0015), so the mail and the LMS
          // button lead to the same place. `paymentUrl` only exists for
          // document kinds with online payment enabled — hence the fallback.
          const payLink = row.paymentUrl || row.viewUrl;
          return payLink ? (
            <OpenButton
              size={ButtonSizes.sm}
              variant="outline"
              onClick={() => window.open(payLink, '_blank', 'noopener,noreferrer')}>
              {t('invoices.open')}
            </OpenButton>
          ) : (
            // Tooltip on a wrapping span so it surfaces even though the button is
            // disabled (a disabled control swallows pointer events).
            <Tooltip title={t('invoices.viewUnavailable')}>
              <span>
                <OpenButton size={ButtonSizes.sm} variant="outline" disabled>
                  {t('invoices.open')}
                </OpenButton>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [t],
  );

  const rowClassName = (row: IStudentInvoice): string => {
    if (row.isOverdue) {
      return 'row--overdue';
    }
    if ((row.outstanding ?? 0) > 0) {
      return 'row--unpaid';
    }
    return '';
  };

  return (
    <InvoicesPageWrapper>
      <InvoicesHeader>
        <PageTitle>{t('invoices.title')}</PageTitle>
        <PageInfo>{t('invoices.info')}</PageInfo>
      </InvoicesHeader>

      {error ? (
        <Alert type="error" showIcon message={t('invoices.loadError')} />
      ) : (
        <>
          <ConfigProvider theme={{ token: { colorPrimary: `${Colors.main}` } }}>
            <StyledTable<IStudentInvoice>
              rowKey="fakturowniaId"
              loading={loading}
              columns={columns}
              dataSource={invoices}
              pagination={false}
              scroll={{ x: 'max-content' }}
              rowClassName={rowClassName}
              locale={{ emptyText: t('invoices.empty') }}
            />
          </ConfigProvider>

          {totalsByCurrency.length ? (
            <TotalOwed>
              <TotalLabel>{t('invoices.totalOwed')}</TotalLabel>
              <TotalAmounts>
                {totalsByCurrency.map(([currency, amount]) => (
                  <TotalAmount key={currency}>{formatMoney(amount, currency)}</TotalAmount>
                ))}
              </TotalAmounts>
            </TotalOwed>
          ) : null}
        </>
      )}
    </InvoicesPageWrapper>
  );
});

const InvoicesPageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const InvoicesHeader = styled.div`
  margin: 32px 0;

  ${tablet} {
    margin: 40px 0 32px 0;
  }

  ${desktop} {
    margin: 54px 0 32px 0;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  ${tablet} {
    ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  }

  ${desktop} {
    ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.regular })}
  }
`;

const DueCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  /* The "overdue: N days" line under the due date renders red and small. */
  && .ant-badge-status-text {
    color: ${Colors.signal_red};
    font-size: 12px;
  }
`;

const AmountCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OpenButton = styled(Button)`
  width: auto;

  ${mobile} {
    width: 100%;
  }
`;

const TotalOwed = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: flex-end;
  gap: 8px 16px;
  width: 100%;
  margin-top: 0;
  padding-top: 16px;
  border-top: 2px solid ${Colors.additional_gray};
`;

const TotalLabel = styled.span`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const TotalAmounts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
`;

const TotalAmount = styled.span`
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
  color: ${Colors.signal_red};
`;

// "&&" doubles the styled-components class so every rule beats antd v5's own
// cssinjs selectors (which are prefixed with .ant-table-wrapper) regardless of
// style-tag injection order.
const StyledTable = styled(Table)`
  && .ant-table-thead > tr > th {
    background: transparent;
    color: ${Colors.gray_dark};
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.4px;
    border-bottom: 2px solid ${Colors.additional_gray};
  }

  /* antd draws a vertical column separator as th::before with a heavily
     :not()-qualified selector — !important is the sane way to out-rank it. */
  && .ant-table-thead > tr > th::before {
    display: none !important;
  }

  && .ant-table-tbody > tr > td {
    padding: 16px;
    border-bottom: 1px solid ${Colors.additional_gray};
  }

  /* Overdue rows: light red tint derived from the themed token (no hardcoded
     hex, dark-theme safe). */
  && .ant-table-tbody > tr.row--overdue > td {
    background: color-mix(in srgb, ${Colors.signal_red} 6%, transparent);
  }

  /* Explicit hover so antd's row-hover background doesn't repaint the tint
     gray — slightly stronger mix keeps the hover affordance. */
  && .ant-table-tbody > tr.row--overdue:hover > td {
    background: color-mix(in srgb, ${Colors.signal_red} 10%, transparent);
  }

  && .ant-table-tbody > tr.row--overdue > td:first-child {
    box-shadow: inset 3px 0 0 0 ${Colors.signal_red};
  }

  /* Unpaid (not yet overdue): no fill, just the yellow left accent. */
  && .ant-table-tbody > tr.row--unpaid > td:first-child {
    box-shadow: inset 3px 0 0 0 ${Colors.main};
  }

  .ant-tag {
    border-radius: 999px;
    padding: 2px 12px;
    border: none;
    font-weight: 500;
  }
` as typeof Table;
